import React, { useContext, useState, useRef, useEffect } from 'react';
import { View, GridColumn, Button, Checkbox, colors } from '@fairygodboss/ui';
import window from 'global/window';
import { css } from 'styled-components';
import { CommentBox } from './CommentBox';
import {
    CommunityContext,
    PageContext,
} from '../../../../../../contexts/ContextCreator';
import ResponsivePicture from '../../../../../Elements/ResponsivePicture/ResponsivePicture';

const UserPicture = () => {
    const { getUser } = useContext(PageContext);
    const { username, logo } = getUser();

    const breakpoints = {
        mobile: {
            width: 48,
            transforms: {
                quality: 'auto',
                crop: 'scale',
                width: '48px',
                format: 'auto',
            },
        },
    };

    return (
        <View
            width="48px"
            height="48px"
            mr="16px"
            borderRadius="50%"
            overflow="hidden"
        >
            <ResponsivePicture
                cloudinaryID={logo || '/v1/avatar_black_160_vtg7lw.svg'}
                alt={`${username} image`}
                disableHeight
                breakpoints={breakpoints}
            />
        </View>
    );
};

export const CommentInputSection = ({
    isLoggedIn,
    type,
    hash,
    showProfilePicture,
    cancelEdit,
    onPost,
    editView,
    content,
    expandComment,
    level,
    placeholder,
    editorRef: _editorRef,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isAnonimous, setIsAnonimous] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentPlainText, setCommentPlainText] = useState('');
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        const cleanText = commentText.replace(/<\/?[^>]+(>|$)/g, '');
        setCommentPlainText(cleanText);
    }, [commentText]);

    const { submitComment, editCommunityContent } = useContext(
        CommunityContext
    );

    let editorRef = useRef();
    if (_editorRef) {
        editorRef = _editorRef;
    }

    useEffect(() => {
        if (isFocused && !isLoggedIn && editorRef.current) {
            editorRef.current?.blur();
        }
    }, [isFocused, isLoggedIn]);

    useEffect(() => {
        if (expandComment) {
            setTimeout(() => {
                if (editorRef.current) {
                    const editor = editorRef.current?.getEditor();
                    if (editor) {
                        editorRef.current.focus();
                    }
                }
            }, 100);
        }
    }, [expandComment]);

    useEffect(() => {
        if (editView) {
            setTimeout(() => {
                if (editorRef.current) {
                    const editor = editorRef.current?.getEditor();
                    if (editor) {
                        const _cleanText = content.comment?.replace(
                            /<\/?[^>]+(>|$)/g,
                            ''
                        );
                        setCommentText(_cleanText);
                        editor.setText(_cleanText);
                        editorRef.current.focus();
                    }
                }
            }, 100);
            if (content.isAnon) {
                setIsAnonimous(true);
            }
        }
    }, [editView, content]);

    const submitText = async () => {
        if (!commentPlainText.length) {
            return;
        }

        setErrors([]);

        if (editView) {
            if (editorRef.current) {
                const editor = editorRef.current?.getEditor();
                const { innerHTML } = editor.root;
                const text = editor.getText();
                const payload = {
                    ...content,
                    anon: isAnonimous,
                    isAnon: isAnonimous,
                    didEdit: 'edit',
                    comment: `${text}`,
                    rawHtml: innerHTML,
                    title: text,
                };
                editCommunityContent('community-comment', payload, hash);
            }
        }

        if (!editView) {
            const result = await submitComment(hash, commentText, isAnonimous);
            if (result?.success === false) {
                setErrors(
                    result.errors?.length
                        ? result.errors
                        : ['Something went wrong. Please try your comment again.']
                );
                return;
            }
        }

        if (editorRef.current) {
            const editor = editorRef.current?.getEditor();
            editor.setText('');
        }
        if (onPost) {
            onPost();
        }

        setCommentText('');
        setIsAnonimous(false);
    };

    let postButtonWidth = editView ? 'calc(50% - 8px)' : '139px';
    if (window?.innerWidth < 388 && !editView && level > 2) {
        postButtonWidth = '100px';
    }

    return (
        <>
            <View display="flex" justifyContent="space-between">
                {isLoggedIn && showProfilePicture ? <UserPicture /> : null}
                <CommentBox
                    hasLeftImage={isLoggedIn && showProfilePicture}
                    placeholder={placeholder}
                    type={type}
                    hash={hash}
                    setActiveState={setIsFocused}
                    setCommentText={setCommentText}
                    commentText={commentText}
                    editorRef={editorRef}
                    isHeaderComment
                />
            </View>
            {isFocused || commentText.length || editView || expandComment ? (
                <View
                    display={{
                        _: editView ? 'block' : '-webkit-box',
                        md: '-webkit-box',
                    }}
                    justifyContent="end"
                    py="16px"
                    alignItems="center"
                >
                    {
                        <View pb={{ _: editView ? '16px' : '0px', md: '0px' }}>
                            <Checkbox
                                onClick={() => {
                                    setIsAnonimous(!isAnonimous);
                                }}
                                checked={isAnonimous}
                                label="Post anonymously"
                                size="medium"
                                css={css({
                                    div: {
                                        zIndex: 0,
                                        height: '20px',
                                        width: '20px',
                                        borderRadius: '3px',
                                        borderColor: `${colors.primary[60]} !important`,
                                    },
                                })}
                            />
                        </View>
                    }
                    {editView ? (
                        <Button
                            width={{ _: 'calc(50% - 8px)', md: '83px' }}
                            height="40px"
                            ml={{ _: '0px', md: '24px' }}
                            variant="secondary"
                            onPress={() => cancelEdit()}
                        >
                            Cancel
                        </Button>
                    ) : null}
                    <Button
                        width={{
                            _: postButtonWidth,
                            md: '139px',
                        }}
                        height="40px"
                        ml={{ _: '16px', md: editView ? '16px' : '24px' }}
                        onPress={() => submitText()}
                    >
                        {editView ? 'Save changes' : 'Post'}
                    </Button>
                </View>
            ) : null}
            {errors.length > 0 ? (
                <View color="#C81E1E" fontSize="14px">
                    {errors.map((error) => (
                        <div key={error}>{error}</div>
                    ))}
                </View>
            ) : null}
        </>
    );
};

export const CommentsHeader = ({
    commentCount,
    type,
    hash,
    isLoggedIn,
    onPost,
    editorRef,
    isCommunityDiscussionType,
}) => {
    return (
        <GridColumn
            size={{ xs: 12, sm: 12, md: 7.8, lg: 7.6 }}
            pt={{ _: '24px', sm: '32px' }}
            pb="24px"
            css={{
                paddingLeft: '0px',
                paddingRight: '0px',
            }}
            {...(isCommunityDiscussionType && {
                itemProp: 'interactionStatistic',
                itemScope: 'itemscope',
                itemType: 'https://schema.org/InteractionCounter',
            })}
        >
            {isCommunityDiscussionType && (
                <>
                    <meta
                        itemProp="interactionType"
                        content="https://schema.org/CommentAction"
                    />
                    <meta
                        itemProp="userInteractionCount"
                        content={commentCount}
                    />
                </>
            )}
            <View
                fontFamily="Open Sans"
                fontWeight={700}
                fontSize="32px"
                lineHeight="44px"
                pb="16px"
                {...(isCommunityDiscussionType && {
                    itemProp: 'userInteractionCount',
                    content: commentCount,
                })}
            >
                {commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}
            </View>
            <CommentInputSection
                placeholder={
                    commentCount !== 0
                        ? 'Join the conversation...'
                        : 'Be the first to comment'
                }
                onPost={onPost}
                isLoggedIn={isLoggedIn}
                type={type}
                hash={hash}
                showProfilePicture
                editorRef={editorRef}
            />
        </GridColumn>
    );
};
